import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AppContext';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Page shown when user clicks verification link from email
export const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  // Ref so a new refreshUser identity never re-runs the one-shot verification.
  const refreshUserRef = useRef(refreshUser);
  refreshUserRef.current = refreshUser;

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('No verification token found');
      return;
    }

    fetch(`${API}/api/auth/verify-email?token=${token}`)
      .then(res => res.json())
      .then(data => {
        if (data.message && !data.detail) {
          setStatus('success');
          setMessage(data.message);
          if (refreshUserRef.current) refreshUserRef.current();
        } else {
          setStatus('error');
          setMessage(data.detail || 'Verification failed');
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage('Verification failed. Please try again.');
      });
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#050505' }}>
      <div className="w-full max-w-md text-center">
        {status === 'verifying' && (
          <div className="space-y-4">
            <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto" />
            <h1 className="text-2xl font-bold text-white">Verifying your email...</h1>
          </div>
        )}
        {status === 'success' && (
          <div className="space-y-6">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <h1 className="text-2xl font-bold text-white">Email Verified!</h1>
            <p className="text-zinc-400">{message}</p>
            <Button
              onClick={() => navigate('/')}
              className="bg-white hover:bg-zinc-200 text-black font-semibold py-3 px-8 rounded-full"
              data-testid="verified-continue-btn"
            >
              Continue to AUDIVUE
            </Button>
          </div>
        )}
        {status === 'error' && (
          <div className="space-y-6">
            <XCircle className="w-16 h-16 text-red-500 mx-auto" />
            <h1 className="text-2xl font-bold text-white">Verification Failed</h1>
            <p className="text-zinc-400">{message}</p>
            <Button
              onClick={() => navigate('/login')}
              className="bg-white hover:bg-zinc-200 text-black font-semibold py-3 px-8 rounded-full"
            >
              Back to Login
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Screen shown to logged-in users who haven't verified email yet
export const EmailVerificationGate = () => {
  const { user, axiosInstance, logout } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResend = async () => {
    setSending(true);
    try {
      await axiosInstance.post('/auth/resend-verification');
      setSent(true);
      toast.success('Verification email sent!');
    } catch {
      toast.error('Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#050505' }}>
      <div className="w-full max-w-md text-center space-y-6">
        <div className="w-20 h-20 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto">
          <Mail className="w-10 h-10 text-orange-500" />
        </div>
        <h1 className="text-2xl font-bold text-white">Check Your Email</h1>
        <p className="text-zinc-400 leading-relaxed">
          We sent a verification link to <span className="text-white font-medium">{user?.email}</span>. 
          Please verify your email to access AUDIVUE.
        </p>
        
        <div className="space-y-3 pt-4">
          <Button
            onClick={handleResend}
            disabled={sending || sent}
            className="w-full bg-white hover:bg-zinc-200 text-black font-semibold py-3 rounded-full"
            data-testid="resend-verification-btn"
          >
            {sending ? 'Sending...' : sent ? 'Email Sent! Check Your Inbox' : 'Resend Verification Email'}
          </Button>
          <button
            onClick={logout}
            className="text-sm text-zinc-500 hover:text-white transition-colors"
            data-testid="verification-logout-btn"
          >
            Sign out and use a different email
          </button>
        </div>

        <p className="text-xs text-zinc-600 pt-4">
          Don't see the email? Check your spam folder.
        </p>
      </div>
    </div>
  );
};
